import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./ChangePassword.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const readStoredUser = () => {
  const raw =
    localStorage.getItem("auth_user") || sessionStorage.getItem("auth_user");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getAuthToken = () =>
  localStorage.getItem("auth_token") ||
  sessionStorage.getItem("auth_token") ||
  localStorage.getItem("token") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("accessToken") ||
  "";

// Save token/user in whichever storage you’re currently using
function persistAuth(token, user) {
  const hasSession =
    sessionStorage.getItem("auth_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("accessToken");

  const hasLocal =
    localStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken");

  const target = hasSession ? sessionStorage : localStorage;
  const other = hasSession ? localStorage : sessionStorage;

  // Prefer consistent keys (auth_token/auth_user) but also keep legacy keys in sync
  if (token) {
    target.setItem("auth_token", token);
    target.setItem("token", token);
    target.setItem("accessToken", token);

    other.removeItem("auth_token");
    other.removeItem("token");
    other.removeItem("accessToken");
  }

  if (user) {
    target.setItem("auth_user", JSON.stringify(user));
    other.removeItem("auth_user");
  }

  // If neither existed before, default to localStorage
  if (!hasSession && !hasLocal) {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("token", token);
    localStorage.setItem("accessToken", token);
    localStorage.setItem("auth_user", JSON.stringify(user));
  }
}

export default function ChangePassword() {
  const nav = useNavigate();
  const { login } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [loading, setLoading] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const canSubmit = useMemo(() => {
    const c = String(currentPassword || "").trim();
    const n = String(newPassword || "").trim();
    const k = String(confirm || "").trim();
    return c.length >= 1 && n.length >= 8 && n === k;
  }, [currentPassword, newPassword, confirm]);

  const submit = async (e) => {
    e.preventDefault();
    setOkMsg("");
    setErrMsg("");

    const token = getAuthToken();
    if (!token) {
      setErrMsg("You are not logged in. Please login again.");
      return;
    }

    if (!canSubmit) {
      if (String(newPassword || "").trim().length < 8) {
        setErrMsg("New password must be at least 8 characters.");
      } else if (String(newPassword || "").trim() !== String(confirm || "").trim()) {
        setErrMsg("New password and confirm password do not match.");
      } else {
        setErrMsg("Enter your current password and a valid new password.");
      }
      return;
    }

    setLoading(true);

    try {
      // 1) Change password using current token
      const resp = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: currentPassword.trim(),
          newPassword: newPassword.trim(),
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || `Request failed (${resp.status}).`);
      }

      // 2) Best case: backend returns updated token/user
      if (data?.token && data?.user) {
        const updatedUser = {
          ...data.user,
          mustChangePassword: false,
          must_change_password: false,
        };

        // update storage + AuthContext
        persistAuth(data.token, updatedUser);
        login(data.token, updatedUser);

        setOkMsg("Password updated. Redirecting...");
        setCurrentPassword("");
        setNewPassword("");
        setConfirm("");

        nav("/", { replace: true });
        return;
      }

      // 3) If backend does NOT return token/user, re-login using stored email + new password
      const storedUser = readStoredUser();
      const email = storedUser?.email;

      if (!email) {
        // Fallback: at least update stored user flags so UI can proceed if your app reads from storage
        const patched = storedUser
          ? { ...storedUser, mustChangePassword: false, must_change_password: false }
          : null;

        if (patched) {
          persistAuth(token, patched);
          login(token, patched);
        }

        setOkMsg("Password updated. Redirecting...");
        nav("/", { replace: true });
        return;
      }

      const relog = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: newPassword.trim() }),
      });

      const relogData = await relog.json().catch(() => ({}));
      if (!relog.ok || !relogData?.success || !relogData?.token) {
        throw new Error(
          relogData?.message ||
            "Password updated, but re-login failed. Please login again."
        );
      }

      const updatedUser = {
        ...(relogData.user || storedUser || {}),
        mustChangePassword: false,
        must_change_password: false,
      };

      persistAuth(relogData.token, updatedUser);
      login(relogData.token, updatedUser);

      setOkMsg("Password updated. Redirecting...");
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");

      nav("/", { replace: true });
    } catch (err) {
      setErrMsg(err?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-card">
        <div className="cp-head">
          <h1 className="cp-title">Change Password</h1>
          <p className="cp-subtitle">
            You must update your password before using the platform.
          </p>
        </div>

        <form className="cp-form" onSubmit={submit}>
          <div className="cp-field">
            <label>Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter temporary password"
            />
          </div>

          <div className="cp-grid">
            <div className="cp-field">
              <label>New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Min 8 characters"
              />
            </div>

            <div className="cp-field">
              <label>Confirm new password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter new password"
              />
            </div>
          </div>

          {okMsg ? <div className="cp-alert ok">{okMsg}</div> : null}
          {errMsg ? <div className="cp-alert err">{errMsg}</div> : null}

          <div className="cp-actions">
            <button className="cp-btn" type="submit" disabled={!canSubmit || loading}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
